import Combobox from "@/components/Combobox";
import InputError from "@/components/InputError";
import SkeletonRow from "@/components/SkeletonRow";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  listAkreditasi,
  listKebutuhanKhusus,
  listLokasiKuliah,
  listSize,
} from "@/constants";
import { useDebounce } from "@/hooks/useDebounce";
import useFilePreview from "@/hooks/useFilePreview";
import useSessionStorage from "@/hooks/useSessionStorage";
import { useUser } from "@/hooks/useUser";
import {
  getBiodata,
  getListAgama,
  getListAlatTransportasi,
  getListJenisTinggal,
  getListNegara,
  getListPerguruanTinggi,
  getListPerguruanTinggiProdi,
  getListSekolah,
  getListWilayah,
} from "@/services/getData";
import {
  Agama,
  AlatTransportasi,
  BiodataSate,
  DataPendaftaran,
  DataSiswa,
  JenisTinggal,
  LokasiKuliah,
  Negara,
  Option,
  PerguruanTinggi,
  PerguruanTinggiProdi,
  Sekolah,
  Size,
  Wilayah,
} from "@/types";
import { AxiosError } from "axios";
import { SetStateAction, useEffect, useRef, useState } from "react";
import { FaPlus } from "react-icons/fa6";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

export default function Biodata({
  form,
  setForm,
  error,
  file,
  setFile,
}: {
  form: BiodataSate;
  setForm: React.Dispatch<SetStateAction<BiodataSate>>;
  error: AxiosError<{
    message?: string;
    errors?: Record<string, string[]>;
  }> | null;
  file: File | null;
  setFile: React.Dispatch<SetStateAction<File | null>>;
}) {
  const { user, isPending: isPendingUser } = useUser();
  const preview = useFilePreview(file);
  const [searchParams] = useSearchParams();
  // const sessionPeriode?.id_sys_periode = searchParams.get("periode");
  const jenjangParams = searchParams.get("jenjang");

  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const uploadRef = useRef<HTMLInputElement | null>(null);
  const [openListSekolah, setOpenListSekolah] = useState(false);
  const [openAsalUniversitas, setOpenAsalUniversitas] = useState(false);
  const [openAsalProdi, setOpenAsalProdi] = useState(false);
  const [openProvinsi, setOpenProvinsi] = useState(false);
  const [openKabupaten, setOpenKabupaten] = useState(false);
  const [openKecamatan, setOpenKecamatan] = useState(false);
  const [openNegara, setOpenNegara] = useState(false);
  const [openAlatTransportasi, setOpenAlatTransportasi] = useState(false);
  const [idFeederProvinsi, setIdFeederProvinsi] = useState("");
  const [idFeederKabupaten, setIdFeederKabupaten] = useState("");
  const [searchQuerySekolah, setSearchQuerySekolah] = useState("");
  const [searchQueryProvinsi, setSearchQueryProvinsi] = useState("");
  const [searchQueryKabupaten, setSearchQueryKabupaten] = useState("");
  const [searchQueryKecamatan, setSearchQueryKecamatan] = useState("");
  const [searchQueryUniversitas, setSearchQueryUniversitas] = useState("");
  // const [selectedAsalUniversitas, setSelectedAsalUniversitas] =
  //   useState<PerguruanTinggiGroup | null>(null);

  const debouncedQueryUniversitas = useDebounce(searchQueryUniversitas, 1000);
  const debouncedSearchQuerySekolah = useDebounce(searchQuerySekolah, 1000);

  const { data: dataSekolah, isPending: isPendingSekolah } = useQuery({
    queryKey: ["listSekolah", debouncedSearchQuerySekolah, form.id_ref_sekolah],
    queryFn: () =>
      getListSekolah(
        "1",
        "10",
        debouncedSearchQuerySekolah
          ? debouncedSearchQuerySekolah
          : form.id_ref_sekolah || "",
      ),
    select: (data) =>
      data?.data?.map((sekolah: Sekolah) => ({
        label: `${sekolah.nama_sekolah} (${sekolah.npsn_sekolah})`,
        value: `${sekolah.id_ref_sekolah}`,
        bentuk_pendidikan: sekolah.bentuk_pendidikan,
      })),
  });

  const { data: dataProvinsi, isPending: isPendingProvinsi } = useQuery({
    queryKey: ["listProvinsi", form.provinsi, searchQueryProvinsi],
    queryFn: () =>
      getListWilayah(
        10,
        "1",
        undefined,
        searchQueryProvinsi ? undefined : form.provinsi,
        searchQueryProvinsi,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
        id_feeder: `${wilayah?.id_feeder}`,
      })),
  });

  const {
    data: dataKabupaten,
    refetch: refetchKabupaten,
    isPending: isPendingKabupaten,
  } = useQuery({
    queryKey: [
      "listKabupaten",
      idFeederProvinsi,
      form.kabupaten,
      searchQueryKabupaten,
    ],
    queryFn: () =>
      getListWilayah(
        10,
        undefined,
        idFeederProvinsi,
        searchQueryKabupaten ? undefined : form.kabupaten,
        searchQueryKabupaten,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
        id_feeder: `${wilayah.id_feeder}`,
      })),
    enabled: !!idFeederProvinsi,
  });

  const {
    data: dataKecamatan,
    refetch: refetchKecamatan,
    isPending: isPendingKecamatan,
  } = useQuery({
    queryKey: [
      "listKecamatan",
      idFeederKabupaten,
      searchQueryKecamatan ? undefined : form.kecamatan,
      searchQueryKecamatan,
    ],
    queryFn: () =>
      getListWilayah(
        10,
        undefined,
        idFeederKabupaten,
        form.kecamatan,
        searchQueryKecamatan,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
      })),
    enabled: !!idFeederKabupaten,
  });

  const { data: dataNegara } = useQuery({
    queryKey: ["listNegara"],
    queryFn: () => getListNegara(),
    select: (data) =>
      data?.map((negara: Negara) => ({
        label: negara.nama_negara,
        value: `${negara.id_ref_negara}`,
      })),
  });

  const { data: dataAgama } = useQuery({
    queryKey: ["listAgama"],
    queryFn: () => getListAgama(),
  });

  const { data: dataJenisTinggal } = useQuery({
    queryKey: ["listJenisTinggal"],
    queryFn: () => getListJenisTinggal(),
  });
  const selectedJenisTinggal =
    dataJenisTinggal &&
    dataJenisTinggal?.find((item: JenisTinggal) => {
      return item.id_ref_jenis_tinggal == form.id_ref_jenis_tinggal;
    });

  const { data: dataAlatTransportasi } = useQuery({
    queryKey: ["listAlatTransportasi"],
    queryFn: () => getListAlatTransportasi(),
    select: (data) =>
      data?.map((alatTransportasi: AlatTransportasi) => ({
        label: alatTransportasi.nama_alat_transportasi,
        value: `${alatTransportasi.id_ref_alat_transportasi}`,
      })),
  });

  const { data: dataSiswa, isPending: isPendingDataSiswa } =
    useQuery<DataSiswa>({
      queryKey: ["dataSiswa", user?.id, sessionPeriode?.id_sys_periode],
      queryFn: () => getBiodata(sessionPeriode?.id_sys_periode || ""),
      enabled: !!user && !!sessionPeriode?.id_sys_periode,
    });
  const dataPendaftaran: DataPendaftaran | undefined =
    dataSiswa?.data_pendaftaran?.[0] || undefined;

  // const { data: optionsPt, isPending: isPendingOptionsPt } = useQuery({
  //   queryKey: [
  //     "listPerguruanTinggiProdiGroup",
  //     debouncedQueryUniversitas,
  //     form.asal_pt,
  //   ],
  //   queryFn: () =>
  //     getListPerguruanTinggiProdiGroup(
  //       "1",
  //       "10",
  //       debouncedQueryUniversitas
  //         ? debouncedQueryUniversitas
  //         : (form.asal_pt as string)
  //     ),
  //   select: (data) =>
  //     data?.map((item: PerguruanTinggiGroup) => ({
  //       label: item.nama_perguruan_tinggi,
  //       value: `${item.kode_perguruan_tinggi}`,
  //       list_prodi: item.list_prodi,
  //     })),
  // });

  const { data: optionsPt, isPending: isPendingOptionsPt } = useQuery({
    queryKey: ["listPerguruanTinggi", debouncedQueryUniversitas, form.asal_pt],
    queryFn: () =>
      getListPerguruanTinggi(
        "1",
        "10",
        debouncedQueryUniversitas ? debouncedQueryUniversitas : form.asal_pt,
      ),
    select: (data) => {
      if (!data) return [];
      return data?.map((item: PerguruanTinggi) => ({
        label: item.nama_perguruan_tinggi,
        value: `${item.id_feeder}`,
        list_prodi: item.pt_prodi,
      }));
    },
  });

  const { data: dataAsalProdi } = useQuery({
    queryKey: ["listAsalProdi", form.asal_pt],
    queryFn: () =>
      getListPerguruanTinggiProdi("1", "1000", form.asal_pt || "", false),
    select: (data) => {
      return data?.map((item: PerguruanTinggiProdi) => ({
        label: `${item.nama_program_studi} (${item.nama_jenjang_pendidikan})`,
        value: `${item.id_feeder}`,
        nama_jenjang_pendidikan: item.nama_jenjang_pendidikan,
      }));
    },
    enabled: !!form.asal_pt,
  });

  const optionsAsalProdi = dataAsalProdi ? dataAsalProdi : [];
  // const optionsAsalProdi = dataAsalProdi
  //   ? dataAsalProdi?.filter((item: Option) => {
  //       return (
  //         item.nama_jenjang_pendidikan === "S1" ||
  //         item.nama_jenjang_pendidikan === "D4"
  //       )
  //     })
  //   : []

  useEffect(() => {
    if (form.provinsi) refetchKabupaten();
  }, [form.provinsi, refetchKabupaten]);

  useEffect(() => {
    if (form.kabupaten) refetchKecamatan();
  }, [form.kabupaten, refetchKecamatan]);

  useEffect(() => {
    if (!dataSiswa || !sessionPeriode?.id_sys_periode) return;

    setForm((prev) => ({
      ...prev,
      id_sys_periode: sessionPeriode?.id_sys_periode || "",
      ...dataSiswa,
      pilihan_lokasi_kuliah:
        (dataSiswa.data_pendaftaran?.[0]?.data_pilihan_prodi?.[0]?.pilihan_lokasi_kuliah?.toLowerCase() as LokasiKuliah) ??
        "",
      asal_pt: dataPendaftaran?.asal_pt,
      akreditasi_pt_asal: dataPendaftaran?.akreditasi_pt_asal,
      asal_prodi: dataPendaftaran?.asal_prodi,
      akreditasi_prodi_asal: dataPendaftaran?.akreditasi_prodi_asal,
      tahun_ijazah_terakhir: dataPendaftaran?.tahun_ijazah_terakhir,
      nilai_IPK: dataPendaftaran?.nilai_IPK,
      is_bekerja: Number(dataPendaftaran?.is_bekerja) === 1 ? true : false,
      tempat_bekerja: dataPendaftaran?.tempat_bekerja,
      penghasilan_bekerja: dataPendaftaran?.penghasilan_bekerja,
    }));
  }, [dataSiswa, dataPendaftaran, setForm, sessionPeriode?.id_sys_periode]);

  useEffect(() => {
    const selectedProvinsi = dataProvinsi?.find(
      (provinsi: Option) => provinsi?.value == form.provinsi,
    );
    if (selectedProvinsi) setIdFeederProvinsi(selectedProvinsi?.id_feeder);

    const selectedKabupaten = dataKabupaten?.find(
      (kabupaten: Option) => kabupaten?.value == form.kabupaten,
    );
    if (selectedKabupaten) setIdFeederKabupaten(selectedKabupaten?.id_feeder);
  }, [
    setIdFeederProvinsi,
    setIdFeederKabupaten,
    dataProvinsi,
    dataKabupaten,
    form,
  ]);

  const isKelasDenpasar =
    dataSiswa?.data_pendaftaran?.[0]?.periode?.sys_prodi?.[0]
      ?.is_kelas_denpasar ?? false;

  const isSMA =
    dataPendaftaran?.jenjang_pendidikan_terakhir_daftar_rpl === "sma";

  return (
    <div>
      <h1 className="mb-5 font-bold text-xl">Biodata</h1>
      {isPendingUser || isPendingDataSiswa ? (
        <div>
          <section className="grid lg:grid-cols-2 grid-cols-1 gap-y-3 gap-x-5">
            {Array(16)
              .fill(null)
              .map((_, index) => {
                return <SkeletonRow key={index} />;
              })}
          </section>
        </div>
      ) : (
        <>
          <div className="mb-5 mx-auto text-center">
            <div className="flex justify-center">
              {preview ? (
                <div
                  className="w-[12rem] aspect-[3/4] border border-dashed border-black rounded-xl hover:bg-secondary duration-300 cursor-pointer"
                  onClick={() => {
                    if (uploadRef.current) {
                      uploadRef.current.click();
                    }
                  }}
                >
                  <img
                    src={preview}
                    alt="Preview"
                    className="w-[12rem] aspect-[3/4] rounded-xl"
                  />
                </div>
              ) : form.path_foto ? (
                <div
                  className="w-[12rem] aspect-[3/4] border border-black rounded-xl hover:bg-secondary duration-300 cursor-pointer"
                  onClick={() => {
                    if (uploadRef.current) {
                      uploadRef.current.click();
                    }
                  }}
                >
                  <img
                    src={form.path_foto}
                    alt="foto"
                    className="w-[12rem] aspect-[3/4] rounded-xl"
                  />
                </div>
              ) : (
                <div
                  className="cursor-pointer group"
                  onClick={() => {
                    if (uploadRef.current) {
                      uploadRef.current.click();
                    }
                  }}
                >
                  <div className="p-5 w-[12rem] aspect-[3/4] border border-dashed border-black rounded-xl flex justify-center items-center hover:bg-secondary duration-300">
                    <div>
                      <FaPlus className="mx-auto" size={25} />
                      <p className="font-semibold text-sm text-muted-foreground">
                        Upload Foto*
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <input
              ref={uploadRef}
              type="file"
              accept="image/png, image/jpeg, image/jpg, image/gif"
              className="hidden"
              onChange={(event) => {
                const files = event.target.files;
                if (files && files[0]) {
                  const file = files[0];
                  setFile(file);
                }
              }}
            />
            <div className="my-3 text-sm font-semibold text-muted-foreground">
              <p>{file && file.name}</p>
              {(file || dataSiswa?.path_foto) && (
                <p>Klik foto untuk mengganti</p>
              )}
            </div>

            <InputError text={error?.response?.data?.errors?.foto?.join(",")} />
          </div>

          <div>
            <section className="grid lg:grid-cols-2 grid-cols-1 gap-y-3 gap-x-5">
              {isKelasDenpasar ? (
                <div>
                  <Label>Lokasi Kuliah</Label>
                  <Select
                    value={form.pilihan_lokasi_kuliah || ""}
                    onValueChange={(value) => {
                      setForm((prev) => ({
                        ...prev,
                        pilihan_lokasi_kuliah: value as LokasiKuliah,
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={"Pilih Lokasi Kuliah"}>
                        {form.pilihan_lokasi_kuliah &&
                          listLokasiKuliah?.find(
                            (value) => value === form.pilihan_lokasi_kuliah,
                          )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {listLokasiKuliah?.map((value, index: number) => (
                        <SelectItem
                          key={index}
                          value={`${value.toLowerCase()}`}
                        >
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <InputError
                    text={error?.response?.data?.errors?.pilihan_lokasi_kuliah?.join(
                      ",",
                    )}
                  />
                </div>
              ) : (
                ""
              )}
              <div>
                <Label required>Nama Lengkap (Tanpa Gelar)</Label>
                <Input
                  required
                  value={form.nama_lengkap || ""}
                  placeholder="Ketik nama lengkap (tanpa gelar)..."
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      nama_lengkap: event.target.value,
                    }))
                  }
                />
                <p className="text-muted-foreground text-sm">
                  Nama lengkap harus sesuai dengan ijazah terakhir, tanpa gelar!
                </p>
                <InputError
                  text={error?.response?.data?.errors?.nama_lengkap?.join(",")}
                />
              </div>
              <div>
                <Label required>Email</Label>
                <Input readOnly value={dataSiswa?.email || ""} />
              </div>
              <div>
                <Label required>NIK / KTP</Label>
                <Input readOnly value={dataSiswa?.nik || ""} />
              </div>
              <div>
                <Label>NISN</Label>
                <Input
                  maxLength={10}
                  value={form.nisn || ""}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, nisn: event.target.value }))
                  }
                  placeholder="Ketik nisn..."
                />
                <InputError
                  text={error?.response?.data?.errors?.nisn?.join(",")}
                />
              </div>

              <div>
                <Label required>Tanggal Lahir</Label>
                <Input
                  required
                  type="date"
                  value={form.tanggal_lahir || ""}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      tanggal_lahir: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.tanggal_lahir?.join(",")}
                />
              </div>
              <div>
                <Label required>Tempat Lahir</Label>
                <Input
                  required
                  value={form.tempat_lahir || ""}
                  placeholder="Ketik tempat lahir..."
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      tempat_lahir: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.tempat_lahir?.join(",")}
                />
              </div>

              <div>
                <Label required>No Telepon/Hp</Label>
                <Input
                  required
                  maxLength={15}
                  value={form.handphone || ""}
                  placeholder="Ketik no handphone..."
                  onChange={(event) => {
                    const value = event.target.value;
                    const numericValue = value.replace(/[^0-9]/g, "");
                    setForm((prev) => ({
                      ...prev,
                      handphone: numericValue,
                    }));
                  }}
                />

                <InputError
                  text={error?.response?.data?.errors?.handphone?.join(",")}
                />
              </div>

              <div>
                <Label required>Agama</Label>
                <Select
                  value={form.id_ref_agama || ""}
                  onValueChange={(value) => {
                    setForm((prev) => ({
                      ...prev,
                      id_ref_agama: value,
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={"Pilih Agama"}>
                      {form.id_ref_agama &&
                        dataAgama?.find(
                          (agama: Agama) =>
                            agama.id_ref_agama == String(form.id_ref_agama),
                        )?.nama_agama}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {dataAgama?.map((agama: Agama, index: number) => (
                      <SelectItem key={index} value={`${agama.id_ref_agama}`}>
                        {agama.nama_agama}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <InputError
                  text={error?.response?.data?.errors?.id_ref_agama?.join(",")}
                />
              </div>

              <div>
                <Label>NPWP</Label>
                <Input
                  minLength={15}
                  maxLength={15}
                  value={form.npwp || ""}
                  placeholder="Ketik npwp..."
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      npwp: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.npwp?.join(",")}
                />
              </div>

              <div>
                <Label required>Jenis Kelamin</Label>
                <Select
                  value={form.jenis_kelamin || ""}
                  onValueChange={(value) => {
                    setForm((prev) => ({
                      ...prev,
                      jenis_kelamin: value as "L" | "P",
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={"Pilih Jenis Kelamin"}>
                      {form.jenis_kelamin &&
                        ["L", "P"]?.find(
                          (value) => value === form.jenis_kelamin,
                        )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {["L", "P"]?.map((value, index) => (
                      <SelectItem key={index} value={`${value}`}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <InputError
                  text={error?.response?.data?.errors?.jenis_kelamin?.join(",")}
                />
              </div>
            </section>

            <section className="grid lg:grid-cols-2 grid-cols-1 gap-y-3 gap-x-5">
              <div>
                <Label required>Berat Badan</Label>
                <Input
                  type="number"
                  required
                  placeholder="Ketik berat badan..."
                  value={form.berat_badan === 0 ? "" : Number(form.berat_badan)}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      berat_badan: Number(event.target.value),
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.berat_badan?.join(",")}
                />
              </div>
              <div>
                <Label required>Tinggi Badan</Label>
                <Input
                  type="number"
                  required
                  placeholder="Ketik tinggi badan..."
                  value={
                    form.tinggi_badan === 0 ? "" : Number(form.tinggi_badan)
                  }
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      tinggi_badan: Number(event.target.value),
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.tinggi_badan?.join(",")}
                />
              </div>

              <div>
                <Label required>Ukuran Jas</Label>
                <Select
                  value={form.ukuran_baju || ""}
                  onValueChange={(value: string) => {
                    setForm((prev) => ({
                      ...prev,
                      ukuran_baju: value as Size,
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={"Pilih Ukuran"}>
                      {form.ukuran_baju &&
                        listSize?.find((value) => value === form.ukuran_baju)}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {listSize?.map((value, index) => (
                      <SelectItem key={index} value={`${value}`}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <InputError
                  text={error?.response?.data?.errors?.ukuran_baju?.join(",")}
                />
              </div>
              <div>
                <Label required>Alat Transportasi</Label>
                <p className="italic text-muted-foreground text-sm">
                  Isi dengan alat transportasi yang akan digunakan selama kuliah
                </p>
                <Combobox
                  open={openAlatTransportasi}
                  setOpen={setOpenAlatTransportasi}
                  value={form.id_ref_alat_transportasi || ""}
                  setValue={(newValue) =>
                    setForm((prev) => ({
                      ...prev,
                      id_ref_alat_transportasi: newValue,
                    }))
                  }
                  options={dataAlatTransportasi}
                  placeholder="Pilih Alat Transportasi"
                />
                <InputError
                  text={error?.response?.data?.errors?.id_ref_alat_transportasi?.join(
                    ",",
                  )}
                />
              </div>
              <div>
                <Label required>Kewarganegaraan</Label>
                <Combobox
                  open={openNegara}
                  setOpen={setOpenNegara}
                  value={form.id_ref_negara || ""}
                  setValue={(newValue) =>
                    setForm((prev) => ({
                      ...prev,
                      id_ref_negara: newValue,
                    }))
                  }
                  options={dataNegara}
                  placeholder="Pilih Kewarganegaraan"
                />
                <InputError
                  text={error?.response?.data?.errors?.id_ref_negara?.join(",")}
                />
              </div>
              <div>
                <Label required>Alamat</Label>
                <Input
                  required
                  placeholder="Ketik alamat tempat tinggal..."
                  value={form.jalan || ""}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      jalan: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.jalan?.join(",")}
                />
              </div>
              <div>
                <Label>RT</Label>
                <Input
                  type="number"
                  value={form.rt || ""}
                  placeholder="Ketik rt berapa..."
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      rt: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.rt?.join(",")}
                />
              </div>
              <div>
                <Label>RW</Label>
                <Input
                  type="number"
                  value={form.rw || ""}
                  placeholder="Ketik rw berapa..."
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      rw: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.rw?.join(",")}
                />
              </div>

              <div>
                <Label required>Provinsi</Label>
                <Combobox
                  serverSide
                  open={openProvinsi}
                  setOpen={setOpenProvinsi}
                  value={form.provinsi || ""}
                  setValue={(newValue) => {
                    setForm((prev) => ({
                      ...prev,
                      provinsi: newValue,
                      kabupaten: "",
                      kecamatan: "",
                    }));
                    const selectedProvinsi = dataProvinsi?.find(
                      (provinsi: Option) => provinsi?.value == newValue,
                    );
                    setIdFeederProvinsi(selectedProvinsi?.id_feeder);
                  }}
                  options={dataProvinsi}
                  placeholder="Pilih provinsi"
                  onChange={(event) =>
                    setSearchQueryProvinsi(event.target.value)
                  }
                  searchQueryValue={searchQueryProvinsi || ""}
                  isPending={isPendingProvinsi}
                />
                <InputError
                  text={error?.response?.data?.errors?.provinsi?.join(",")}
                />
              </div>
              <div>
                <Label required>Kabupaten</Label>
                <Combobox
                  serverSide
                  open={openKabupaten}
                  setOpen={setOpenKabupaten}
                  value={form.kabupaten || ""}
                  setValue={(newValue) => {
                    setForm((prev) => ({
                      ...prev,
                      kabupaten: newValue,
                      kecamatan: "",
                    }));
                    const selectedKabupaten = dataKabupaten?.find(
                      (kabupaten: Option) => kabupaten?.value == newValue,
                    );
                    setIdFeederKabupaten(selectedKabupaten?.id_feeder);
                  }}
                  options={dataKabupaten}
                  placeholder="Pilih kabupaten"
                  onChange={(event) =>
                    setSearchQueryKabupaten(event.target.value)
                  }
                  searchQueryValue={searchQueryKabupaten || ""}
                  isPending={isPendingKabupaten}
                />
                <InputError
                  text={error?.response?.data?.errors?.kabupaten?.join(",")}
                />
              </div>

              <div>
                <Label required>Kecamatan</Label>
                <Combobox
                  serverSide
                  open={openKecamatan}
                  setOpen={setOpenKecamatan}
                  value={form.kecamatan || ""}
                  setValue={(newValue) =>
                    setForm((prev) => ({
                      ...prev,
                      kecamatan: newValue,
                    }))
                  }
                  options={dataKecamatan}
                  placeholder="Pilih kecamatan"
                  onChange={(event) =>
                    setSearchQueryKecamatan(event.target.value)
                  }
                  searchQueryValue={searchQueryKecamatan || ""}
                  isPending={isPendingKecamatan}
                />
                <InputError
                  text={error?.response?.data?.errors?.kecamatan?.join(",")}
                />
              </div>
              <div>
                <Label required>Desa/Kelurahan</Label>
                <Input
                  placeholder="Ketik desa/kelurahan..."
                  value={form.kelurahan || ""}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      kelurahan: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.kelurahan?.join(",")}
                />
              </div>
              <div>
                <Label required>Kode Pos</Label>
                <Input
                  maxLength={5}
                  placeholder="Ketik kode pos..."
                  value={form.kode_pos || ""}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      kode_pos: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.kode_pos?.join(",")}
                />
              </div>
              <div>
                <Label required>Jenis Tinggal</Label>
                <Select
                  value={form.id_ref_jenis_tinggal || ""}
                  onValueChange={(value) => {
                    setForm((prev) => ({
                      ...prev,
                      id_ref_jenis_tinggal: value,
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={"Pilih Jenis Tinggal"}>
                      {form.id_ref_jenis_tinggal &&
                        dataJenisTinggal?.find(
                          (jenisTinggal: JenisTinggal) =>
                            jenisTinggal.id_ref_jenis_tinggal ==
                            String(form.id_ref_jenis_tinggal),
                        )?.nama_jenis_tinggal}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {dataJenisTinggal?.map(
                      (jenisTinggal: JenisTinggal, index: number) => (
                        <SelectItem
                          key={index}
                          value={`${jenisTinggal.id_ref_jenis_tinggal}`}
                        >
                          {jenisTinggal.nama_jenis_tinggal}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <InputError
                  text={error?.response?.data?.errors?.id_ref_jenis_tinggal?.join(
                    ",",
                  )}
                />
              </div>

              <div>
                <Label required>Nama Ibu Kandung</Label>
                <Input
                  required
                  value={form.nama_ibu_kandung || ""}
                  placeholder="Ketik nama ibu kandung..."
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      nama_ibu_kandung: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.nama_ibu_kandung?.join(
                    ",",
                  )}
                />
              </div>

              <div>
                <Label required>Sekolah Asal</Label>
                <Combobox
                  serverSide
                  open={openListSekolah}
                  setOpen={setOpenListSekolah}
                  value={form.id_ref_sekolah || ""}
                  setValue={(newValue) => {
                    // const selected = dataSekolah?.find((option: Option) => {
                    //   return String(option.value) == String(newValue)
                    // })

                    setForm((prev) => ({
                      ...prev,
                      id_ref_sekolah: newValue,
                      // id_ref_jurusan_sekolah: "",
                    }));
                  }}
                  onChange={(event) =>
                    setSearchQuerySekolah(event.target.value)
                  }
                  options={dataSekolah}
                  placeholder="Pilih Sekolah"
                  isPending={isPendingSekolah}
                  searchQueryValue={searchQuerySekolah || ""}
                />
                <p className="text-muted-foreground font-semibold">
                  Cari berdasarkan nama sekolah atau NPSN Sekolah!
                </p>
                <InputError
                  text={error?.response?.data.errors?.id_ref_sekolah?.join(",")}
                />
              </div>

              {selectedJenisTinggal &&
              selectedJenisTinggal?.nama_jenis_tinggal.startsWith("Panti") ? (
                <div>
                  <Label required>Nama Panti Asuhan</Label>
                  <Input
                    required
                    placeholder="Ketik nama panti asuhan..."
                    value={form.nama_panti_asuhan || ""}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        nama_panti_asuhan: event.target.value,
                      }))
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.nama_panti_asuhan?.join(
                      ",",
                    )}
                  />
                </div>
              ) : (
                ""
              )}

              {isSMA ? (
                ""
              ) : (
                <>
                  <div>
                    <Label required>
                      Universitas / Institusi{" "}
                      {jenjangParams === "S2"
                        ? "S1/D4"
                        : jenjangParams === "S3"
                          ? "S2"
                          : ""}{" "}
                    </Label>

                    <Combobox
                      serverSide
                      open={openAsalUniversitas}
                      setOpen={setOpenAsalUniversitas}
                      value={form.asal_pt || ""}
                      setValue={(newValue) => {
                        // const selected = optionsPt?.find((option: Option) => {
                        //   return option?.value === newValue;
                        // });
                        // setSelectedAsalUniversitas(selected);
                        setForm((prev) => ({
                          ...prev,
                          asal_pt: newValue,
                        }));
                      }}
                      onChange={(event) =>
                        setSearchQueryUniversitas(event.target.value)
                      }
                      options={optionsPt}
                      placeholder="Pilih Asal Perguruan Tinggi"
                      isPending={isPendingOptionsPt}
                      searchQueryValue={searchQueryUniversitas || ""}
                    />

                    <InputError
                      text={error?.response?.data?.errors?.asal_pt?.join(",")}
                    />
                  </div>

                  <div>
                    <Label required>
                      Akreditasi Universitas / Institusi{" "}
                      {jenjangParams === "S2"
                        ? "S1/D4"
                        : jenjangParams === "S3"
                          ? "S2"
                          : ""}{" "}
                    </Label>
                    <Select
                      value={form.akreditasi_pt_asal || ""}
                      onValueChange={(value: string) => {
                        setForm((prev) => ({
                          ...prev,
                          akreditasi_pt_asal: value,
                        }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={"Pilih Akreditasi"}>
                          {form.akreditasi_pt_asal &&
                            listAkreditasi?.find(
                              (value) => value === form.akreditasi_pt_asal,
                            )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {listAkreditasi?.map((value, index) => (
                          <SelectItem key={index} value={`${value}`}>
                            {value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <InputError
                      text={error?.response?.data?.errors?.akreditasi_pt_asal?.join(
                        ",",
                      )}
                    />
                  </div>

                  {form.asal_pt ? (
                    <>
                      <div>
                        <Label required>Program Studi</Label>
                        <Combobox
                          open={openAsalProdi}
                          setOpen={setOpenAsalProdi}
                          value={form.asal_prodi || ""}
                          setValue={(newValue) => {
                            setForm((prev) => ({
                              ...prev,
                              asal_prodi: newValue,
                            }));
                          }}
                          options={optionsAsalProdi}
                          placeholder="Pilih Asal Program Studi"
                          isPending={isPendingOptionsPt}
                        />
                        <InputError
                          text={error?.response?.data?.errors?.asal_prodi?.join(
                            ",",
                          )}
                        />
                      </div>
                      <div>
                        <Label required>
                          Akreditasi Program Studi{" "}
                          {jenjangParams === "S2"
                            ? "S1/D4"
                            : jenjangParams === "S3"
                              ? "S2"
                              : ""}{" "}
                        </Label>
                        <Select
                          value={form.akreditasi_prodi_asal || ""}
                          onValueChange={(value: string) => {
                            setForm((prev) => ({
                              ...prev,
                              akreditasi_prodi_asal: value,
                            }));
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={"Pilih Akreditasi"}>
                              {form.akreditasi_prodi_asal &&
                                listAkreditasi?.find(
                                  (value) =>
                                    value === form.akreditasi_prodi_asal,
                                )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {listAkreditasi?.map((value, index) => (
                              <SelectItem key={index} value={`${value}`}>
                                {value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <InputError
                          text={error?.response?.data?.errors?.akreditasi_prodi_asal?.join(
                            ",",
                          )}
                        />
                      </div>
                    </>
                  ) : (
                    ""
                  )}
                </>
              )}

              <div>
                <Label required>Tahun Ijazah Terakhir</Label>
                <Input
                  minLength={4}
                  maxLength={4}
                  value={form.tahun_ijazah_terakhir || ""}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      tahun_ijazah_terakhir: event.target.value,
                    }))
                  }
                  placeholder="Ketik tahun ijazah terakhir..."
                />
                <InputError
                  text={error?.response?.data?.errors?.tahun_ijazah_terakhir?.join(
                    ",",
                  )}
                />
              </div>

              {isSMA ? (
                ""
              ) : (
                <div>
                  <Label required>Nilai IPK</Label>
                  <Input
                    type="number"
                    value={form.nilai_IPK || ""}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        nilai_IPK: event.target.value,
                      }))
                    }
                    placeholder="Ketik nilai ipk..."
                  />
                  <InputError
                    text={error?.response?.data?.errors?.nilai_IPK?.join(",")}
                  />
                </div>
              )}

              <div>
                <Label required>Status Bekerja</Label>
                <div className="mt-1 flex gap-5">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={form.is_bekerja === false}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({
                          ...prev,
                          is_bekerja: checked ? false : undefined,
                        }))
                      }
                    />
                    <Label>Belum Bekerja</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={form.is_bekerja === true}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({
                          ...prev,
                          is_bekerja: checked ? true : undefined,
                        }))
                      }
                    />
                    <Label>Sudah Bekerja</Label>
                  </div>
                </div>
                <InputError
                  text={error?.response?.data?.errors?.is_bekerja?.join(",")}
                />
              </div>
            </section>

            <section className="mt-3">
              {form.is_bekerja ? (
                <div className="grid lg:grid-cols-2 grid-cols-1 gap-3">
                  <div>
                    <Label>Instansi Bekerja</Label>
                    <Input
                      value={form.tempat_bekerja || ""}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          tempat_bekerja: event.target.value,
                        }))
                      }
                      placeholder="Ketik instansi bekerja..."
                    />
                    <InputError
                      text={error?.response?.data?.errors?.tempat_bekerja?.join(
                        ",",
                      )}
                    />
                  </div>
                  <div>
                    <Label>Penghasilan Kotor</Label>
                    <Input
                      type="number"
                      value={form.penghasilan_bekerja || ""}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          penghasilan_bekerja: event.target.value,
                        }))
                      }
                      placeholder="Ketik penghasilan kotor..."
                    />
                    <InputError
                      text={error?.response?.data?.errors?.penghasilan_bekerja?.join(
                        ",",
                      )}
                    />
                  </div>
                </div>
              ) : (
                ""
              )}

              <Label>Kebutuhan Khusus</Label>
              <div className="mt-3 grid lg:grid-cols-4 grid-cols-2 gap-3">
                {listKebutuhanKhusus.map((value, index) => {
                  return (
                    <div key={index} className="flex gap-3">
                      <Checkbox
                        checked={
                          form?.kebutuhan_khusus?.includes(value) || false
                        }
                        onCheckedChange={(isChecked) => {
                          setForm((prev) => {
                            const updatedKebutuhanKhusus = isChecked
                              ? [...(prev.kebutuhan_khusus || []), value]
                              : (prev.kebutuhan_khusus || []).filter(
                                  (item) => item !== value,
                                );
                            return {
                              ...prev,
                              kebutuhan_khusus: updatedKebutuhanKhusus,
                            };
                          });
                        }}
                      />
                      <Label>{value}</Label>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
